import { ThunkDispatch } from "@reduxjs/toolkit";
import * as React from "react";
import { AnyAction } from "redux";
import { useDispatch } from "react-redux";

import { useTypedSelector } from "@hooks";
import {
  advisorApi,
  useChooseAdvisorChoiceMutation,
  useStartAdvisorSessionMutation,
} from "@services";
import {
  resetAdvisorSession,
  restoreAdvisorSession,
  revealPendingAdvisorTurn,
  setAdvisorDraft,
  setAdvisorLoading,
} from "@store/advisor";
import { IAdvisorChoice } from "@types";

import {
  clearCachedSessionId,
  clearCachedTranscript,
  loadCachedSessionId,
  loadCachedTranscript,
  saveCachedSessionId,
  saveCachedTranscript,
} from "./storage";

/**
 * Owns the advisor conversation's feature logic: bootstrapping/restoring a session, persisting
 * it to sessionStorage, and the choose/submit/restart handlers. All resulting state lives in the
 * advisor slice (via advisorApi's matchers) — this hook only triggers requests and reads state,
 * it never holds conversation data itself.
 */
export function useAdvisorSession({
  offerId,
  companyId,
  active,
}: {
  offerId?: number;
  companyId?: number;
  active: boolean;
}) {
  // Not useTypedDispatch: the store builds `middleware` as a plain array literal, so AppDispatch
  // loses RTK's thunk typing — and the bootstrap below dispatches an `initiate` thunk.
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const { sessionId, entries, choices, busy, loading, error, pending, draft } =
    useTypedSelector(state => state.advisor);

  const [startAdvisorSession] = useStartAdvisorSessionMutation();
  const [chooseAdvisorChoice] = useChooseAdvisorChoiceMutation();

  // Guards the bootstrap effect from re-running for an offer it already started/restored for —
  // it fires once per offer, only once the drawer is actually opened. Also doubles as "which
  // offer does the slice's current conversation belong to" — see the reset below.
  const bootstrapped = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    if (offerId === undefined || companyId === undefined) return;
    if (bootstrapped.current === offerId) return;

    // The slice is app-global state, not scoped to a component instance — switching offers
    // (even without ever closing the drawer, or with the OfferModeller/AdvisorChat instance
    // reused across a route param change) would otherwise leave the previous offer's
    // conversation sitting in the slice for the new offer to inherit. Clear it the moment we
    // notice the offer changed, before even checking whether the drawer is open, so the
    // previous offer's messages can never bleed into view.
    dispatch(resetAdvisorSession());
    if (!active) return; // don't spend a network call until the drawer is actually opened
    bootstrapped.current = offerId;

    const cachedSessionId = loadCachedSessionId(companyId, offerId);
    if (!cachedSessionId) {
      startAdvisorSession({ offer_id: offerId, company_id: companyId });
      return;
    }

    dispatch(setAdvisorLoading(true));
    const cachedTranscript = loadCachedTranscript(cachedSessionId);
    if (cachedTranscript) {
      dispatch(
        restoreAdvisorSession({
          sessionId: cachedSessionId,
          entries: cachedTranscript.entries,
          choices: cachedTranscript.choices,
        })
      );
    }

    // A one-shot validation read, deliberately not subscribed: a subscribed query stays
    // registered under this session id for the life of the panel, so a later invalidation would
    // refetch it long after a reset moved the conversation elsewhere.
    dispatch(
      advisorApi.endpoints.fetchAdvisorSession.initiate(
        { session_id: cachedSessionId },
        { subscribe: false, forceRefetch: true }
      )
    )
      .unwrap()
      .catch(() => {
        // A stale mapping — the cached session expired or is otherwise gone server-side.
        // Self-heal by clearing it and starting a fresh session instead of leaving the panel
        // stuck on an error the user can't do anything about.
        clearCachedSessionId(companyId, offerId);
        clearCachedTranscript(cachedSessionId);
        dispatch(resetAdvisorSession());
        startAdvisorSession({ offer_id: offerId, company_id: companyId });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, offerId, companyId]);

  // Mirror the offer -> session mapping and the running transcript into sessionStorage so a
  // refresh or a panel reopen restores exactly where the conversation left off. The advisor API
  // only ever returns the CURRENT turn (no history endpoint), so this cache is what makes
  // history survive a reload at all — not an optimization, the only copy of it.
  React.useEffect(() => {
    if (offerId !== undefined && companyId !== undefined && sessionId)
      saveCachedSessionId(companyId, offerId, sessionId);
  }, [companyId, offerId, sessionId]);

  React.useEffect(() => {
    if (sessionId && entries.length > 0)
      saveCachedTranscript(sessionId, entries, choices);
  }, [sessionId, entries, choices]);

  const onChoose = (choice: IAdvisorChoice) => {
    if (!sessionId) return;
    chooseAdvisorChoice({ session_id: sessionId, choice_id: choice.choice_id });
  };

  const onSubmitText = (submitChoiceId: string) => {
    if (!sessionId || busy) return;
    const text = draft.trim();
    if (!text) return;
    chooseAdvisorChoice({
      session_id: sessionId,
      choice_id: submitChoiceId,
      text,
    });
  };

  const onDraftChange = (value: string) => dispatch(setAdvisorDraft(value));
  const onRevealPending = () => dispatch(revealPendingAdvisorTurn());

  const onRestart = () => {
    if (offerId !== undefined && companyId !== undefined)
      clearCachedSessionId(companyId, offerId);
    if (sessionId) clearCachedTranscript(sessionId);
    dispatch(resetAdvisorSession());
    if (offerId !== undefined && companyId !== undefined) {
      startAdvisorSession({ offer_id: offerId, company_id: companyId });
    }
  };

  const lastAdvisorEntry = [...entries]
    .reverse()
    .find(entry => entry.role === "advisor");
  const freeText =
    lastAdvisorEntry?.role === "advisor" && !pending
      ? lastAdvisorEntry.payload.free_text
      : null;

  return {
    entries,
    choices,
    busy,
    loading,
    error,
    pending,
    draft,
    freeText,
    onChoose,
    onSubmitText,
    onDraftChange,
    onRevealPending,
    onRestart,
  };
}
